import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { ShowcaseItem } from '../../../types';
import { navigateTo } from '../../../utils/router';
import { useListApi } from '../../../hooks/useApi';
import showcaseService from '../../../services/api/showcaseService';
import { getCategoryLabel, getIndustryLabel, getTechnologyLabel, getTechnologyLabels } from '../../../utils/dictConverter';
import './ShowcasePage.css';

const ShowcasePage: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // 从URL参数初始化状态
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '全部');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  
  // 专利池条件检索状态
  const [patentPoolConditions, setPatentPoolConditions] = useState({
    title: '',
    company: '',
    industry: '',
    technology: ''
  });

  // 分类配置（包含图标和对应的字典值）
  const categories = useMemo(() => [
    { key: '全部', label: '全部', icon: '🏆', dictValue: undefined },
    { key: '专利技术', label: '专利技术', icon: '📋', dictValue: 'patent_technology' },
    { key: '商标品牌', label: '商标品牌', icon: '🏷️', dictValue: 'trademark_brand' },
    { key: '版权作品', label: '版权作品', icon: '📖', dictValue: 'copyright_works' },
    { key: '专利池', label: '专利池', icon: '🏊', dictValue: 'patent_pool' },
    { key: '专利检索', label: '专利检索', icon: '🔍', dictValue: 'patent_search' },
  ], []);

  // 使用API钩子获取展示数据 - 加载全部数据
  const showcaseApi = useListApi(
    async (params) => {
      const result = await showcaseService.getShowcaseList({ 
        ...params, 
        current: 1,
        size: 1000, // 加载全部数据
      });
      
      // 处理API返回的数据结构
      const apiData = result as any;
      let rows: any[] = [];
      let total = 0;
      
      if (apiData.records && Array.isArray(apiData.records)) {
        rows = apiData.records;
        total = apiData.total || apiData.records.length;
      } else if (apiData.rows && Array.isArray(apiData.rows)) {
        rows = apiData.rows;
        total = apiData.total || apiData.rows.length;
      } else if (apiData.data && Array.isArray(apiData.data)) {
        rows = apiData.data;
        total = apiData.total || apiData.data.length;
      } else if (Array.isArray(apiData)) {
        rows = apiData;
        total = apiData.length;
      }
      
      return { data: { rows, total } };
    },
    { pageSize: 1000 } // 设置为较大的值以加载全部数据
  );

  // 更新URL参数
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory !== '全部') {
      params.set('category', selectedCategory);
    }
    if (searchTerm) {
      params.set('search', searchTerm);
    }
    setSearchParams(params);
  }, [selectedCategory, searchTerm, setSearchParams]);

  // 处理分类筛选
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    // 切换分类时重置专利池条件
    if (category !== '专利池') {
      setPatentPoolConditions({
        title: '',
        company: '',
        industry: '',
        technology: ''
      });
    }
  };

  // 处理搜索
  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  // 处理专利池条件检索
  const handlePatentPoolConditionChange = (field: string, value: string) => {
    setPatentPoolConditions(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 获取展示数据 - useListApi返回的数据结构是 { rows, total }
  const showcaseItems = useMemo(() => {
    if (!showcaseApi.data || !showcaseApi.data.rows) return [];
    return showcaseApi.data.rows;
  }, [showcaseApi.data]);

  const filteredItems = useMemo(() => {
    if (selectedCategory === '全部') {
      // 如果是全部，只应用搜索过滤
      return showcaseItems.filter((item: ShowcaseItem) => {
        if (!searchTerm) return true;
        return item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item as any).companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item as any).company?.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }
    
    return showcaseItems.filter((item: ShowcaseItem) => {
      // 分类匹配
      const selectedCat = categories.find(cat => cat.key === selectedCategory);
      if (!selectedCat) return false;
      
      const itemCategory = item.category || '';
      const categoryStr = itemCategory.toString().toLowerCase();
      
      // 使用字典转换进行匹配
      const itemCategoryLabel = getCategoryLabel(itemCategory).toLowerCase();
      const catLabelLower = selectedCat.label.toLowerCase();
      
      // 直接匹配
      let matchesCategory = 
        itemCategoryLabel === catLabelLower || 
        categoryStr === selectedCat.label.toLowerCase() || 
        categoryStr === selectedCat.key.toLowerCase();
      
      // 包含匹配
      if (!matchesCategory) {
        matchesCategory = 
          itemCategoryLabel.includes(catLabelLower) || 
          categoryStr.includes(selectedCat.label.toLowerCase()) || 
          categoryStr.includes(selectedCat.key.toLowerCase());
      }
      
      // 如果有dictValue，也尝试匹配
      if (!matchesCategory && selectedCat.dictValue) {
        const dictValueLower = selectedCat.dictValue.toLowerCase();
        matchesCategory = categoryStr.includes(dictValueLower);
      }
      
      if (!matchesCategory) return false;
      
      // 如果是专利池，应用条件检索
      if (selectedCategory === '专利池') {
        const itemData = item as any;
        
        // 项目名称匹配
        const matchesTitle = !patentPoolConditions.title || !patentPoolConditions.title.trim() ||
          item.title.toLowerCase().includes(patentPoolConditions.title.trim().toLowerCase());
        
        // 企业名称匹配
        const matchesCompany = !patentPoolConditions.company || !patentPoolConditions.company.trim() ||
          (itemData.companyName?.toLowerCase().includes(patentPoolConditions.company.trim().toLowerCase()) ||
           itemData.company?.toLowerCase().includes(patentPoolConditions.company.trim().toLowerCase()));
        
        // 所属行业匹配 - 支持字典转换
        const matchesIndustry = !patentPoolConditions.industry || !patentPoolConditions.industry.trim() || (() => {
          const conditionIndustry = patentPoolConditions.industry.trim().toLowerCase();
          const itemIndustryLabel = getIndustryLabel(itemData.industry).toLowerCase();
          const itemIndustry = (itemData.industry || '').toLowerCase();
          
          return itemIndustryLabel === conditionIndustry ||
                 itemIndustryLabel.includes(conditionIndustry) ||
                 conditionIndustry.includes(itemIndustryLabel) ||
                 itemIndustry.includes(conditionIndustry) ||
                 conditionIndustry.includes(itemIndustry);
        })();
        
        // 核心技术匹配 - 支持多种格式和字典转换
        const matchesTechnology = !patentPoolConditions.technology || !patentPoolConditions.technology.trim() || (() => {
          const conditionTech = patentPoolConditions.technology.trim().toLowerCase();
          
          // 检查 technology 字段（可能是字符串或逗号分隔）
          if (itemData.technology) {
            if (typeof itemData.technology === 'string') {
              // 逗号分隔的字符串，逐个检查和转换
              const techArray = itemData.technology.split(',').map((t: string) => t.trim()).filter(Boolean);
              for (const tech of techArray) {
                const techLabel = getTechnologyLabel(tech).toLowerCase();
                if (techLabel === conditionTech || 
                    tech.toLowerCase() === conditionTech ||
                    techLabel.includes(conditionTech) ||
                    conditionTech.includes(techLabel) ||
                    tech.toLowerCase().includes(conditionTech) ||
                    conditionTech.includes(tech.toLowerCase())) {
                  return true;
                }
              }
            }
          }
          
          // 检查 keywords 数组
          if (itemData.keywords && Array.isArray(itemData.keywords)) {
            for (const keyword of itemData.keywords) {
              if (!keyword) continue;
              const keywordLabel = getTechnologyLabel(keyword).toLowerCase();
              if (keywordLabel === conditionTech || 
                  keyword.toLowerCase() === conditionTech ||
                  keywordLabel.includes(conditionTech) ||
                  conditionTech.includes(keywordLabel) ||
                  keyword.toLowerCase().includes(conditionTech) ||
                  conditionTech.includes(keyword.toLowerCase())) {
                return true;
              }
            }
          }
          
          return false;
        })();
        
        return matchesTitle && matchesCompany && matchesIndustry && matchesTechnology;
      }
      
      // 其他分类的搜索关键词匹配
      const matchesSearch = !searchTerm || 
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item as any).companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item as any).company?.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesSearch;
    });
  }, [showcaseItems, selectedCategory, searchTerm, patentPoolConditions, categories]);

  // 计算每个分类的数量 - 基于所有展示数据
  const categoryCounts = useMemo(() => {
    if (!showcaseItems || showcaseItems.length === 0) {
      return { '全部': 0, '专利技术': 0, '商标品牌': 0, '版权作品': 0, '专利池': 0, '专利检索': 0 };
    }
    
    // 先计算各个分类的数量（排除"全部"和"专利检索"）
    const counts: Record<string, number> = {};
    
    categories.forEach(cat => {
      if (cat.key !== '全部' && cat.key !== '专利检索') {
        counts[cat.key] = showcaseItems.filter((item: ShowcaseItem) => {
          // 尝试多种匹配方式
          const itemCategory = item.category || '';
          const categoryStr = itemCategory.toString().toLowerCase();
          
          // 使用字典转换进行匹配
          const itemCategoryLabel = getCategoryLabel(itemCategory).toLowerCase();
          const catLabelLower = cat.label.toLowerCase();
          
          // 直接匹配
          if (itemCategoryLabel === catLabelLower || 
              categoryStr === cat.label.toLowerCase() || 
              categoryStr === cat.key.toLowerCase()) {
            return true;
          }
          
          // 包含匹配
          if (itemCategoryLabel.includes(catLabelLower) || 
              categoryStr.includes(cat.label.toLowerCase()) || 
              categoryStr.includes(cat.key.toLowerCase())) {
            return true;
          }
          
          // 如果有dictValue，也尝试匹配
          if (cat.dictValue) {
            const dictValueLower = cat.dictValue.toLowerCase();
            if (categoryStr.includes(dictValueLower)) {
              return true;
            }
          }
          
          return false;
        }).length;
      } else if (cat.key === '专利检索') {
        // 专利检索是特殊功能，可能有特殊处理逻辑，这里先设为0
        counts[cat.key] = 0;
      }
    });
    
    // "全部"的数量应该是所有数据的总数（包括专利检索）
    counts['全部'] = showcaseItems.length;
    
    return counts;
  }, [showcaseItems, categories]);

  // 处理展示项点击
  const handleItemClick = (item: ShowcaseItem) => {
    const itemData = item as any;
    // 检查是否有 video_url 链接（支持多种字段名）
    const videoUrl = itemData.videoUrl || itemData.video_url || '';
    
    // 如果有链接且不为空，在新窗口打开
    if (videoUrl && typeof videoUrl === 'string' && videoUrl.trim()) {
      // 确保链接包含协议
      let url = videoUrl.trim();
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    // 如果没有链接，不跳转（什么都不做）
  };

  // 如果有ID参数，显示详情
  useEffect(() => {
    if (id) {
      const item = showcaseItems.find((item: ShowcaseItem) => item.id.toString() === id);
      if (item) {
      }
    }
  }, [id, showcaseItems]);

  // 加载数据 - 只在展示页面时加载
  useEffect(() => {
    // 检查当前路径是否为展示页面
    const isShowcasePage = location.pathname.includes('/showcase') || location.pathname.includes('/showcases');
    if (!isShowcasePage) {
      return; // 如果不是展示页面，不加载数据
    }
    showcaseApi.execute();
  }, [location.pathname]); // 依赖路径，确保只在展示页面时加载

  return (
    <div className="showcase-page">
      {/* Banner区域 */}
      <div className="showcase-banner">
        <div className="banner-content">
          <div className="banner-text">
            <h1 className="banner-title">精品展示</h1>
            <p className="banner-subtitle">汇聚黄石市优秀知识产权成果，展示创新力量</p>
          </div>
        </div>
      </div>

      {/* 筛选和搜索 */}
      <div className="filters-section">
        <div className="category-filters">
          {categories.map(category => {
            const isActive = selectedCategory === category.key;
            const count = categoryCounts[category.key] || 0;
            
            return (
              <button
                key={category.key}
                className={`filter-btn ${isActive ? 'active' : ''}`}
                onClick={() => handleCategoryChange(category.key)}
                style={{'--btn-color': category.key === '全部' ? '#1890ff' : 
                  category.key === '专利技术' ? '#52c41a' :
                  category.key === '商标品牌' ? '#fa8c16' :
                  category.key === '版权作品' ? '#722ed1' :
                  category.key === '专利池' ? '#13c2c2' :
                  category.key === '专利检索' ? '#eb2f96' : '#1890ff'
                } as React.CSSProperties}
              >
                <span className="btn-icon">{category.icon}</span>
                <span className="btn-text">{category.label}</span>
                {category.key !== '全部' && count > 0 && (
                  <span className="btn-count">{count}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* 内容区域 */}
      <div className="showcase-content">
        <div className="container">
          {/* 标题区域 */}
          <div className="showcase-header">
          <h2 className="showcase-title">
            {selectedCategory === '全部' ? '全部展示' : `${selectedCategory}展示`}
          </h2>
          <p className="showcase-count">
            ({filteredItems.length}项)
          </p>
        </div>

        {/* 专利池条件检索区域 */}
        {selectedCategory === '专利池' && (
          <div className="patent-pool-search-conditions">
            <div className="search-conditions-header">
              <p>请选择检索条件以筛选专利池内容</p>
            </div>
            <div className="search-conditions-content">
              <div className="condition-row">
                <div className="condition-field">
                  <label>项目名称：</label>
                  <input
                    type="text"
                    className="condition-input"
                    placeholder="请输入项目名称"
                    value={patentPoolConditions.title}
                    onChange={(e) => handlePatentPoolConditionChange('title', e.target.value)}
                  />
                </div>
                <div className="condition-field">
                  <label>企业名称：</label>
                  <input
                    type="text"
                    className="condition-input"
                    placeholder="请输入企业名称"
                    value={patentPoolConditions.company}
                    onChange={(e) => handlePatentPoolConditionChange('company', e.target.value)}
                  />
                </div>
                <div className="condition-field">
                  <label>所属行业：</label>
                  <select
                    className="condition-select"
                    value={patentPoolConditions.industry}
                    onChange={(e) => handlePatentPoolConditionChange('industry', e.target.value)}
                  >
                    <option value="">全部</option>
                    <option value="制造业">制造业</option>
                    <option value="医疗健康">医疗健康</option>
                    <option value="旅游业">旅游业</option>
                    <option value="传统工艺">传统工艺</option>
                    <option value="新能源汽车">新能源汽车</option>
                    <option value="信息技术">信息技术</option>
                  </select>
                </div>
                <div className="condition-field">
                  <label>核心技术：</label>
                  <select
                    className="condition-select"
                    value={patentPoolConditions.technology}
                    onChange={(e) => handlePatentPoolConditionChange('technology', e.target.value)}
                  >
                    <option value="">全部</option>
                    <option value="深度学习">深度学习</option>
                    <option value="医学影像">医学影像</option>
                    <option value="人工智能">人工智能</option>
                    <option value="物联网">物联网</option>
                    <option value="数据分析">数据分析</option>
                    <option value="工业4.0">工业4.0</option>
                    <option value="品牌设计">品牌设计</option>
                    <option value="旅游营销">旅游营销</option>
                    <option value="文化传承">文化传承</option>
                    <option value="专利共享">专利共享</option>
                    <option value="大数据">大数据</option>
                    <option value="智能检索">智能检索</option>
                    <option value="知识产权">知识产权</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 展示列表 */}
        <div className={`showcase-grid ${
          filteredItems.length === 1 ? 'single-item' :
          filteredItems.length === 2 ? 'double-item' :
          filteredItems.length === 3 ? 'triple-item' :
          'multiple-items'
        }`}>
          {showcaseApi.loading ? (
            <div className="loading">加载中...</div>
          ) : showcaseApi.error ? (
            <div className="error">加载失败: {showcaseApi.error}</div>
          ) : (
            filteredItems.map((item: ShowcaseItem) => {
              const itemData = item as any; // 扩展类型以支持更多字段
              
              // 字典转换
              const categoryLabel = getCategoryLabel(item.category);
              const industryLabel = getIndustryLabel(itemData.industry);
              
              // 获取技术标签（支持keywords数组和technology字符串）
              let technologyLabels: string[] = [];
              if (itemData.keywords && Array.isArray(itemData.keywords) && itemData.keywords.length > 0) {
                technologyLabels = itemData.keywords.slice(0, 3).map((tech: string) => getTechnologyLabel(tech));
              } else if (itemData.technology && typeof itemData.technology === 'string') {
                technologyLabels = getTechnologyLabels(itemData.technology).slice(0, 3);
              }
              
              // 所有卡片都可以点击查看详情
              const handleCardClick = () => {
                // 如果当前分类是专利池，添加 from=showcase 参数
                const queryParams = selectedCategory === '专利池' ? { from: 'showcase' } : undefined;
                navigateTo.showcaseDetail(navigate, item.id, queryParams);
              };
              
              return (
                <div 
                  key={item.id} 
                  className="showcase-item clickable" 
                  onClick={handleCardClick}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="item-image">
                    {item.image ? (
                      <img src={item.image} alt={item.title} onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const parent = target.parentElement;
                        if (parent) {
                          parent.innerHTML = '<div class="image-fallback"></div>';
                        }
                      }} />
                    ) : (
                      <div className="image-fallback">
                        {/* 图标已移除 */}
                      </div>
                    )}
                  </div>
                  <div className="item-body">
                    <div className="item-header">
                      <h3 className="item-title">{item.title}</h3>
                      <div className="item-badge">{categoryLabel}</div>
                    </div>
                    <p className="item-desc" title={item.description}>
                      {item.description}
                    </p>
                    {technologyLabels.length > 0 && (
                      <div className="item-tech">
                        {technologyLabels.map((techLabel: string, idx: number) => (
                          <span key={idx} className="tech-chip">{techLabel}</span>
                        ))}
                      </div>
                    )}
                    {(itemData.companyName || itemData.company || itemData.industry) && (
                      <div className="item-meta">
                        {(itemData.companyName || itemData.company) && (
                          <div className="meta-company">
                            <span className="company-icon">🏢</span>
                            <span className="company-name">{itemData.companyName || itemData.company}</span>
                          </div>
                        )}
                        {itemData.industry && (
                          <div className="meta-industry">
                            <span className="industry-icon">🏭</span>
                            <span className="industry-name">{industryLabel || itemData.industry}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

          </div>
      </div>
    </div>
  );
};

export default ShowcasePage;